import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { XRControllerModelFactory } from 'three/examples/jsm/webxr/XRControllerModelFactory.js';
import { EventDispatcher } from '../EventDispatcher';
import { debug } from '../potreeState';
import { resourcePath } from '../potreeState';
import { TextSprite } from '../TextSprite';
import { Utils } from '../utils';

/** Loose interface for the Potree viewer used by VR controls */
interface PotreeViewer {
  renderer: THREE.WebGLRenderer;
  scene: {
    view: {
      position: THREE.Vector3;
      direction: THREE.Vector3;
      setView(pos: THREE.Vector3, target: THREE.Vector3): void;
    };
    pointclouds: { boundingBox: THREE.Box3 }[];
    getActiveCamera(): THREE.Camera;
  };
  sceneVR: THREE.Scene;
  getMoveSpeed(): number;
  setMoveSpeed(speed: number): void;
  addEventListener(type: string, listener: () => void): void;
}

/** Extended XR controller with custom properties */
interface VRController extends THREE.Group {
  inputSource?: { gamepad?: Gamepad; [key: string]: unknown };
  start?: { position: THREE.Vector3; rotation: THREE.Euler };
  [key: string]: unknown;
}

/** Debug line helper returned by Utils.debugLine */
interface DebugLine {
  node: THREE.Object3D;
  set(start: THREE.Vector3, end: THREE.Vector3): void;
}

/** Interface for VR control modes */
interface VRMode {
  start(vrControls: VRControls): void;
  end(vrControls: VRControls): void;
  update(vrControls: VRControls, delta: number): void;
}

declare global {
  interface Window {
    vrMenu?: THREE.Object3D;
  }
}

function toScene(vec: THREE.Vector3, ref: THREE.Object3D): THREE.Vector3 {
  const node = ref.clone();
  node.updateMatrix();
  node.updateMatrixWorld();

  const result = vec.clone().applyMatrix4(node.matrix);
  result.z -= 0.8 * node.scale.x;

  return result;
}

function computeMove(vrControls: VRControls, controller: VRController | null): THREE.Vector3 | null {
  if (!controller || !controller.inputSource || !controller.inputSource.gamepad) {
    return null;
  }

  const pad = controller.inputSource.gamepad;

  const axes = pad.axes;
  // [0,1] are for touchpad, [2,3] for thumbsticks?
  let y = 0;
  if (axes.length === 2) {
    y = axes[1];
  } else if (axes.length === 4) {
    y = axes[3];
  }

  y = Math.sign(y) * (2 * y) ** 2;

  let maxSize = 0;
  for (const pc of vrControls.viewer.scene.pointclouds) {
    const size = pc.boundingBox.min.distanceTo(pc.boundingBox.max);
    maxSize = Math.max(maxSize, size);
  }
  const multiplicator = Math.pow(maxSize, 0.5) / 2;

  const scale = vrControls.node.scale.x;
  const moveSpeed = vrControls.viewer.getMoveSpeed();
  const amount = (multiplicator * y * moveSpeed ** 0.5) / scale;

  const rotation = new THREE.Quaternion().setFromEuler(controller.rotation);
  const dir = new THREE.Vector3(0, 0, -1);
  dir.applyQuaternion(rotation);

  const move = dir.clone().multiplyScalar(amount);

  const p1 = vrControls.toScene(controller.position);
  const p2 = vrControls.toScene(controller.position.clone().add(move));

  return p2.clone().sub(p1);
}

class FlyMode implements VRMode {
  moveFactor: number;
  dbgLabel: TextSprite | null;

  constructor(_vrControls: VRControls) {
    this.moveFactor = 1;
    this.dbgLabel = null;
  }

  start(vrControls: VRControls) {
    if (!this.dbgLabel) {
      this.dbgLabel = new TextSprite('abc');
      this.dbgLabel.name = 'debug label';
      vrControls.viewer.sceneVR.add(this.dbgLabel);
      this.dbgLabel.visible = false;
    }
  }

  end(_vrControls: VRControls) {
    // noop
  }

  update(vrControls: VRControls, delta: number) {
    const primary = vrControls.cPrimary;
    const secondary = vrControls.cSecondary;

    let move1 = computeMove(vrControls, primary);
    let move2 = computeMove(vrControls, secondary);

    if (!move1) {
      move1 = new THREE.Vector3();
    }

    if (!move2) {
      move2 = new THREE.Vector3();
    }

    const move = move1.clone().add(move2);

    move.multiplyScalar(-delta * this.moveFactor);
    vrControls.node.position.add(move);

    const scale = vrControls.node.scale.x;

    const camVR = vrControls.viewer.renderer.xr.getCamera();

    const vrPos = camVR.getWorldPosition(new THREE.Vector3());
    const vrDir = camVR.getWorldDirection(new THREE.Vector3());
    vrPos.clone().add(vrDir.multiplyScalar(scale));

    const scenePos = toScene(vrPos, vrControls.node);
    const sceneDir = toScene(vrPos.clone().add(vrDir), vrControls.node).sub(scenePos);
    sceneDir.normalize().multiplyScalar(scale);
    const sceneTarget = scenePos.clone().add(sceneDir);

    vrControls.viewer.scene.view.setView(scenePos, sceneTarget);

    if (debug.message && this.dbgLabel && primary) {
      this.dbgLabel.visible = true;
      this.dbgLabel.setText(debug.message);
      this.dbgLabel.scale.set(0.1, 0.1, 0.1);
      this.dbgLabel.position.copy(primary.position);
    }
  }
}

class TranslationMode implements VRMode {
  controller: VRController | null;
  startPos: THREE.Vector3 | null;
  debugLine: DebugLine | null;

  constructor() {
    this.controller = null;
    this.startPos = null;
    this.debugLine = null;
  }

  start(vrControls: VRControls) {
    this.controller = vrControls.triggered.values().next().value as VRController;
    this.startPos = vrControls.node.position.clone();
  }

  end(_vrControls: VRControls) {
    // noop
  }

  update(vrControls: VRControls, _delta: number) {
    if (!this.controller?.start || !this.startPos) return;

    let start: THREE.Vector3 = this.controller.start.position;
    let end: THREE.Vector3 = this.controller.position;

    start = vrControls.toScene(start);
    end = vrControls.toScene(end);

    const diff = end.clone().sub(start);
    diff.set(-diff.x, -diff.y, -diff.z);

    const pos = new THREE.Vector3().addVectors(this.startPos, diff);

    vrControls.node.position.copy(pos);
  }
}

class RotScaleMode implements VRMode {
  line: DebugLine | null;
  startState: THREE.Object3D | null;
  dbgLabel!: TextSprite;

  constructor() {
    this.line = null;
    this.startState = null;
  }

  start(vrControls: VRControls) {
    if (!this.line) {
      this.line = Utils.debugLine(vrControls.viewer.sceneVR, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0), 0xffff00) as DebugLine;

      this.dbgLabel = new TextSprite('abc');
      this.dbgLabel.scale.set(0.1, 0.1, 0.1);
      vrControls.viewer.sceneVR.add(this.dbgLabel);
    }

    this.line.node.visible = true;

    this.startState = vrControls.node.clone();
  }

  end(_vrControls: VRControls) {
    if (this.line) {
      this.line.node.visible = false;
    }
    this.dbgLabel.visible = false;
  }

  update(vrControls: VRControls, _delta: number) {
    if (!vrControls.cPrimary?.start || !vrControls.cSecondary?.start || !this.startState) return;

    const start_c1 = vrControls.cPrimary.start.position.clone();
    const start_c2 = vrControls.cSecondary.start.position.clone();
    const start_center = start_c1.clone().add(start_c2).multiplyScalar(0.5);
    const start_c1_c2 = start_c2.clone().sub(start_c1);
    const end_c1 = vrControls.cPrimary.position.clone();
    const end_c2 = vrControls.cSecondary.position.clone();
    const end_center = end_c1.clone().add(end_c2).multiplyScalar(0.5);
    const end_c1_c2 = end_c2.clone().sub(end_c1);

    const d1 = start_c1_c2.length();
    const d2 = end_c1_c2.length();

    const angleStart = new THREE.Vector2(start_c1_c2.x, start_c1_c2.z).angle();
    const angleEnd = new THREE.Vector2(end_c1_c2.x, end_c1_c2.z).angle();
    const angleDiff = angleEnd - angleStart;

    const scale = d2 / d1;

    const node = this.startState.clone();
    node.updateMatrix();
    node.matrixAutoUpdate = false;

    const mToOrigin = new THREE.Matrix4().makeTranslation(...(toScene(start_center, this.startState).multiplyScalar(-1).toArray() as [number, number, number]));
    const mToStart = new THREE.Matrix4().makeTranslation(...(toScene(start_center, this.startState).toArray() as [number, number, number]));
    const mRotate = new THREE.Matrix4().makeRotationZ(angleDiff);
    const mScale = new THREE.Matrix4().makeScale(1 / scale, 1 / scale, 1 / scale);

    node.applyMatrix4(mToOrigin);
    node.applyMatrix4(mRotate);
    node.applyMatrix4(mScale);
    node.applyMatrix4(mToStart);

    const oldScenePos = toScene(start_center, this.startState);
    const newScenePos = toScene(end_center, node);
    const toNew = oldScenePos.clone().sub(newScenePos);
    const mToNew = new THREE.Matrix4().makeTranslation(...(toNew.toArray() as [number, number, number]));
    node.applyMatrix4(mToNew);

    node.matrix.decompose(node.position, node.quaternion, node.scale);

    vrControls.node.position.copy(node.position);
    vrControls.node.quaternion.copy(node.quaternion);
    vrControls.node.scale.copy(node.scale);
    vrControls.node.updateMatrix();

    {
      const nodeScale = vrControls.node.scale.x;
      const camVR = vrControls.viewer.renderer.xr.getCamera();

      const vrPos = camVR.getWorldPosition(new THREE.Vector3());
      const vrDir = camVR.getWorldDirection(new THREE.Vector3());
      vrPos.clone().add(vrDir.multiplyScalar(nodeScale));

      const scenePos = toScene(vrPos, this.startState);
      const sceneDir = toScene(vrPos.clone().add(vrDir), this.startState).sub(scenePos);
      sceneDir.normalize().multiplyScalar(nodeScale);
      const sceneTarget = scenePos.clone().add(sceneDir);

      vrControls.viewer.scene.view.setView(scenePos, sceneTarget);
      vrControls.viewer.setMoveSpeed(nodeScale);
    }

    {
      // update "GUI"
      if (this.line) this.line.set(end_c1, end_c2);

      const guiScale = vrControls.node.scale.x;
      this.dbgLabel.visible = true;
      this.dbgLabel.position.copy(end_center);
      this.dbgLabel.setText(`scale: 1 : ${guiScale.toFixed(2)}`);
      this.dbgLabel.scale.set(0.05, 0.05, 0.05);
    }
  }
}

export class VRControls extends EventDispatcher {
  viewer: PotreeViewer;
  enabled: boolean;
  node: THREE.Object3D;
  triggered: Set<VRController>;
  menu: THREE.Object3D | null;
  cPrimary: VRController | null;
  cSecondary: VRController | null;
  mode_fly: FlyMode;
  mode_translate: TranslationMode;
  mode_rotScale: RotScaleMode;
  mode: VRMode;
  scene: { getActiveCamera(): THREE.Camera } | null;
  sceneControls: THREE.Scene;

  constructor(viewer: PotreeViewer) {
    super();

    this.viewer = viewer;
    this.enabled = true;
    this.scene = null;
    this.sceneControls = new THREE.Scene();

    viewer.addEventListener('vr_start', this.onStart.bind(this));
    viewer.addEventListener('vr_end', this.onEnd.bind(this));

    this.node = new THREE.Object3D();
    this.node.up.set(0, 0, 1);
    this.triggered = new Set();

    const xr = viewer.renderer.xr;

    {
      // lights

      const light = new THREE.PointLight(0xffffff, 5, 0, 1);
      light.position.set(0, 2, 0);
      this.viewer.sceneVR.add(light);
    }

    this.menu = null;
    this.cPrimary = null;
    this.cSecondary = null;

    const controllerModelFactory = new XRControllerModelFactory();

    const sg = new THREE.SphereGeometry(1, 32, 32);
    const sm = new THREE.MeshNormalMaterial();

    {
      // setup primary controller
      const controller = xr.getController(0) as unknown as VRController;

      const grip = xr.getControllerGrip(0);
      grip.name = 'grip(0)';

      // ADD CONTROLLERMODEL
      grip.add(controllerModelFactory.createControllerModel(grip));
      this.viewer.sceneVR.add(grip);

      // ADD SPHERE
      const sphere = new THREE.Mesh(sg, sm);
      sphere.scale.set(0.005, 0.005, 0.005);

      controller.add(sphere);
      controller.visible = true;
      this.viewer.sceneVR.add(controller);

      {
        // ADD LINE

        const lineGeometry = new LineGeometry();

        lineGeometry.setPositions([0, 0, -0.15, 0, 0, 0.05]);

        const lineMaterial = new LineMaterial({
          color: 0xff0000,
          linewidth: 2,
          resolution: new THREE.Vector2(1000, 1000),
        });

        const line = new Line2(lineGeometry, lineMaterial);

        controller.add(line);
      }

      // XR controller events are not in Object3DEventMap but exist at runtime
      (controller as unknown as { addEventListener(type: string, listener: (event: { data?: unknown }) => void): void }).addEventListener('connected', function (event) {
        controller.inputSource = event.data as VRController['inputSource'];
        // initInfo(controller);
      });

      (controller as unknown as { addEventListener(type: string, listener: () => void): void }).addEventListener('selectstart', () => {
        this.onTriggerStart(controller);
      });
      (controller as unknown as { addEventListener(type: string, listener: () => void): void }).addEventListener('selectend', () => {
        this.onTriggerEnd(controller);
      });

      this.cPrimary = controller;
    }

    {
      // setup secondary controller
      const controller = xr.getController(1) as unknown as VRController;

      const grip = xr.getControllerGrip(1);

      // ADD CONTROLLER MODEL
      const model = controllerModelFactory.createControllerModel(grip);
      grip.add(model);
      this.viewer.sceneVR.add(grip);

      // ADD SPHERE
      const sphere = new THREE.Mesh(sg, sm);
      sphere.scale.set(0.005, 0.005, 0.005);
      controller.add(sphere);
      controller.visible = true;
      this.viewer.sceneVR.add(controller);

      {
        // ADD LINE

        const lineGeometry = new LineGeometry();

        lineGeometry.setPositions([0, 0, -0.15, 0, 0, 0.05]);

        const lineMaterial = new LineMaterial({
          color: 0xff0000,
          linewidth: 2,
          resolution: new THREE.Vector2(1000, 1000),
        });

        const line = new Line2(lineGeometry, lineMaterial);

        controller.add(line);
      }

      // XR controller events are not in Object3DEventMap but exist at runtime
      (controller as unknown as { addEventListener(type: string, listener: (event: { data?: unknown }) => void): void }).addEventListener('connected', (event) => {
        controller.inputSource = event.data as VRController['inputSource'];
        this.initMenu(controller);
      });

      (controller as unknown as { addEventListener(type: string, listener: () => void): void }).addEventListener('selectstart', () => {
        this.onTriggerStart(controller);
      });
      (controller as unknown as { addEventListener(type: string, listener: () => void): void }).addEventListener('selectend', () => {
        this.onTriggerEnd(controller);
      });

      this.cSecondary = controller;
    }

    this.mode_fly = new FlyMode(this);
    this.mode_translate = new TranslationMode();
    this.mode_rotScale = new RotScaleMode();
    this.mode = this.mode_fly;
    this.setMode(this.mode_fly);
  }

  createSlider(label: string, _min: number, _max: number): THREE.Object3D {
    const sg = new THREE.SphereGeometry(1, 8, 8);
    const cg = new THREE.CylinderGeometry(1, 1, 1, 8);
    const matHandle = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const matScale = new THREE.MeshBasicMaterial({ color: 0xff4444 });
    const matValue = new THREE.MeshNormalMaterial();

    const node = new THREE.Object3D();
    node.name = 'slider';
    const nLabel = new TextSprite(`${label}: 0`);
    const nMax = new THREE.Mesh(sg, matHandle);
    const nMin = new THREE.Mesh(sg, matHandle);
    const nValue = new THREE.Mesh(sg, matValue);
    const nScale = new THREE.Mesh(cg, matScale);

    nLabel.scale.set(0.2, 0.2, 0.2);
    nLabel.position.set(0, 0.35, 0);

    nMax.scale.set(0.02, 0.02, 0.02);
    nMax.position.set(0, 0.25, 0);

    nMin.scale.set(0.02, 0.02, 0.02);
    nMin.position.set(0, -0.25, 0);

    nValue.scale.set(0.02, 0.02, 0.02);
    nValue.position.set(0, 0, 0);

    nScale.scale.set(0.005, 0.5, 0.005);

    node.add(nLabel);
    node.add(nMax);
    node.add(nMin);
    node.add(nValue);
    node.add(nScale);

    return node;
  }

  createInfo(): THREE.Mesh {
    const texture = new THREE.TextureLoader().load(`${resourcePath}/images/vr_controller_help.jpg`);
    const plane = new THREE.PlaneGeometry(1, 1, 1, 1);
    const infoMaterial = new THREE.MeshBasicMaterial({ map: texture });
    const infoNode = new THREE.Mesh(plane, infoMaterial);

    return infoNode;
  }

  initMenu(controller: VRController) {
    if (this.menu) {
      return;
    }

    const node = new THREE.Object3D();
    node.name = 'vr menu';

    // let nSlider = this.createSlider("speed", 0, 1);
    // let nInfo = this.createInfo();

    // // node.add(nSlider);
    // node.add(nInfo);

    // {
    // 	node.rotation.set(-1.5, 0, 0)
    // 	node.scale.set(0.3, 0.3, 0.3);
    // 	node.position.set(-0.2, -0.002, -0.1)

    // 	// nInfo.position.set(0.5, 0, 0);
    // 	nInfo.scale.set(0.8, 0.6, 0);

    // 	// controller.add(node);
    // }

    // node.position.set(-0.3, 1.2, 0.2);
    // node.scale.set(0.3, 0.2, 0.3);
    // node.lookAt(new THREE.Vector3(0, 1.5, 0.1));

    // this.viewer.sceneVR.add(node);

    this.menu = node;

    // window.vrSlider = nSlider;
    window.vrMenu = node;
  }

  toScene(vec: THREE.Vector3): THREE.Vector3 {
    const camVR = this.getCamera();

    const mat = camVR.matrixWorld;
    const result = vec.clone().applyMatrix4(mat);

    return result;
  }

  toVR(vec: THREE.Vector3): THREE.Vector3 {
    const camVR = this.getCamera();

    const mat = camVR.matrixWorld.clone();
    mat.invert();
    const result = vec.clone().applyMatrix4(mat);

    return result;
  }

  setMode(mode: VRMode) {
    if (this.mode === mode) {
      return;
    }

    if (this.mode) {
      this.mode.end(this);
    }

    for (const controller of [this.cPrimary, this.cSecondary]) {
      if (!controller) continue;
      const start = {
        position: controller.position.clone(),
        rotation: controller.rotation.clone(),
      };

      controller.start = start;
    }

    this.mode = mode;
    this.mode.start(this);
  }

  onTriggerStart(controller: VRController) {
    this.triggered.add(controller);

    if (this.triggered.size === 0) {
      this.setMode(this.mode_fly);
    } else if (this.triggered.size === 1) {
      this.setMode(this.mode_translate);
    } else if (this.triggered.size === 2) {
      this.setMode(this.mode_rotScale);
    }
  }

  onTriggerEnd(controller: VRController) {
    this.triggered.delete(controller);

    if (this.triggered.size === 0) {
      this.setMode(this.mode_fly);
    } else if (this.triggered.size === 1) {
      this.setMode(this.mode_translate);
    } else if (this.triggered.size === 2) {
      this.setMode(this.mode_rotScale);
    }
  }

  onStart() {
    const position = this.viewer.scene.view.position.clone();
    const direction = this.viewer.scene.view.direction;
    direction.multiplyScalar(-1);

    const target = position.clone().add(direction);
    target.z = position.z;

    const scale = this.viewer.getMoveSpeed();

    this.node.position.copy(position);
    this.node.lookAt(target);
    this.node.scale.set(scale, scale, scale);
    this.node.updateMatrix();
    this.node.updateMatrixWorld();
  }

  onEnd() {
    // noop
  }

  stop() {
    // noop
  }

  setScene(scene: { getActiveCamera(): THREE.Camera }) {
    this.scene = scene;
  }

  getCamera(): THREE.PerspectiveCamera {
    // reference kept for potential near/far calibration
    // const reference = this.viewer.scene.getActiveCamera();
    const camera = new THREE.PerspectiveCamera();

    // let scale = this.node.scale.x;
    const scale = this.viewer.getMoveSpeed();
    //camera.near = 0.01 / scale;
    camera.near = 0.1;
    camera.far = 1000;
    // camera.near = reference.near / scale;
    // camera.far = reference.far / scale;
    camera.up.set(0, 0, 1);
    camera.lookAt(new THREE.Vector3(0, -1, 0));
    camera.updateMatrix();
    camera.updateMatrixWorld();

    camera.position.copy(this.node.position);
    camera.rotation.copy(this.node.rotation);
    camera.scale.set(scale, scale, scale);
    camera.updateMatrix();
    camera.updateMatrixWorld();
    camera.matrixAutoUpdate = false;
    camera.parent = camera;

    return camera;
  }

  update(delta: number) {
    // if(this.mode === this.mode_fly){
    // 	let ray = new THREE.Ray(origin, direction);

    // 	for(let object of this.selectables){

    // 		if(object.intersectsRay(ray)){
    // 			object.onHit(ray);
    // 		}

    // 	}

    // }

    this.mode.update(this, delta);
  }
}
